import moment from 'moment';
import { toast } from 'react-toastify';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import { Spinner } from '../../../../components/Spinner';
import { useEnv } from '../../../../context/env-context';
import { makeRequest } from '../../../../utils/makeRequest';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import StyledTableCell from '../../../../components/StyledTableCell';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { capitalizeFirstLetter, getTextWidthInOneLine } from '../../../../utils/strings';
import { SYSTEM_JOB_STATUS_BADGE } from '../../../../constants/orchestrator';
import ReactJsonEditor from '../../../../components/ReactJsonEditor';

function DashboardSystemJobs() {
  const { apiOriginOrchestrator } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [spinner, setSpinner] = useState(false);
  const [systemJobs, setSystemJobs] = useState([]);

  const [selectedJobId, setSelectedJobId] = useState(null);

  const getTimeDiff = (a, b) => {
    if (!a || !b) return '---';

    const minutes = moment(a).diff(b, 'minutes');
    const seconds = moment(a).diff(b, 'seconds') - minutes * 60;

    return `${minutes > 9 ? '' : '0'}${minutes}:${seconds > 9 ? '' : '0'}${seconds}`;
  };

  const timeoutRef = useRef(null);

  const getSystemJobs = async indicateLoading => {
    clearTimeout(timeoutRef.current);

    if (indicateLoading) {
      setSpinner(true);
    }

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = { token, url: `${apiOriginOrchestrator}/system_jobs/get`, method: 'GET' };

    try {
      const data = await makeRequest(config);

      setSystemJobs(data.jobs);

      if (data.jobs.some(x => x.status === 'RUNNING' || x.status === 'QUEUE')) {
        timeoutRef.current = setTimeout(() => getSystemJobs(), 3000);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      if (indicateLoading) {
        setSpinner(false);
      }
    }
  };

  useEffect(() => {
    getSystemJobs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderResultsCell = (type, results) => {
    if (!results) return null;

    if (type === 'REWARDS_PROCESS') {
      return (
        <>
          {Object.entries(results).map(([key, value]) => (
            <p className="text-nowrap mb-0" key={key}>
              {`${capitalizeFirstLetter(key)}: ${value}`}
            </p>
          ))}
        </>
      );
    }

    if (type === 'FINCH_SYNC') {
      return (
        <>
          <p className="text-nowrap mb-0">{`Connections: ${
            Object.keys(results.connections).length
          }`}</p>
          <p className="text-nowrap mb-0">{`Employees: ${Object.values(results.connections).reduce(
            (acc, value) => acc + value.employees,
            0,
          )}`}</p>
          <p className="mt-2 mb-0">Pre-match:</p>

          <ul>
            <li>Linked consumers: {results.consumers.pre_linked_employees}</li>
            <li>Non-linked consumers: {results.consumers.non_linked}</li>
          </ul>
          <p className="mt-2 mb-0">Matching results:</p>
          {results.consumers.matching && Object.keys(results.consumers.matching).length > 0 ? (
            <ul>
              {Object.keys(results.consumers.matching).find(x =>
                x.startsWith('match_employee_'),
              ) && (
                <li>
                  Newly matched consumers:{' '}
                  {Object.entries(results.consumers?.matching).reduce((acc, [key, value]) => {
                    if (key.startsWith('match_employee_')) {
                      return value + acc;
                    }

                    return acc;
                  }, 0)}
                </li>
              )}
              {results.consumers.matching.no_match !== undefined && (
                <li>Non-matched consumers: {results.consumers.matching.no_match}</li>
              )}
              {results.consumers.matching.candidate !== undefined && (
                <li>Candidate consumers: {results.consumers.matching.candidate}</li>
              )}
            </ul>
          ) : (
            <p>---</p>
          )}
          <p className="mt-2 mb-0">Post-match processing:</p>
          {results.consumers.update && Object.keys(results.consumers.update).length > 0 ? (
            <ul>
              {results.consumers.update.employee_not_found !== undefined && (
                <li>Employees not found: {results.consumers.update.employee_not_found}</li>
              )}
              {(results.consumers.update.employee_still_active !== undefined ||
                results.consumers.update.employee_still_not_active !== undefined) && (
                <li>
                  No change:{' '}
                  {(results.consumers.update.employee_still_active || 0) +
                    (results.consumers.update.employee_still_not_active || 0)}
                </li>
              )}
              {results.consumers.update.employee_became_in_active !== undefined && (
                <li>Newly terminated: {results.consumers.update.employee_became_in_active}</li>
              )}
              {results.consumers.update.employee_became_active_again !== undefined && (
                <li>Rehires: {results.consumers.update.employee_became_active_again}</li>
              )}
              {results.consumers.update.employee_still_not_active_end_date_changed !==
                undefined && (
                <li>
                  Termination date change:{' '}
                  {results.consumers.update.employee_still_not_active_end_date_changed}
                </li>
              )}
            </ul>
          ) : (
            <p>---</p>
          )}
        </>
      );
    }

    if (type === 'MONTHLY_BRIDGEPOINTS') {
      return (
        <>
          <p className="text-nowrap mb-0">{`Received Full: ${results.receivedFull}`}</p>
          <p className="text-nowrap mb-0">{`Received Partial: ${results.receivedPartial}`}</p>
        </>
      );
    }

    return (
      <ReactJsonEditor
        value={results}
        options={{
          mode: 'view',
          search: false,
          history: false,
          mainMenuBar: false,
          navigationBar: false,
          statusBar: true,
          onClassName: () => 'remove-objects',
        }}
        expandAll
        containerClassName="jsoneditor-transparent-container"
      />
    );
  };

  return (
    <Card className="mt-3">
      <Card.Header className="d-flex justify-content-between">
        System Jobs
        <Button className="bo-button-inline-link" onClick={() => getSystemJobs(true)}>
          Refresh
        </Button>
      </Card.Header>
      <Card.Body>
        <Spinner visible={spinner} text="">
          <TableContainer className="bo-h-500">
            <Table aria-labelledby="tableTitle" aria-label="enhanced table">
              <TableHead>
                <TableRow>
                  <StyledTableCell align="left">Job ID</StyledTableCell>
                  <StyledTableCell align="left">Description</StyledTableCell>
                  <StyledTableCell align="left">Status</StyledTableCell>
                  <StyledTableCell align="left">Init Time</StyledTableCell>
                  <StyledTableCell align="left" className="text-nowrap">
                    Time in Queue
                  </StyledTableCell>
                  <StyledTableCell align="left" className="text-nowrap">
                    Process Time
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {systemJobs
                  .sort((a, b) => moment(b.initTime).valueOf() - moment(a.initTime).valueOf())
                  .map(job => (
                    <>
                      <TableRow
                        key={job.id}
                        hover
                        tabIndex={-1}
                        onClick={() => setSelectedJobId(prev => (prev === job.id ? null : job.id))}
                        className={selectedJobId === job.id && 'bo-table-active-row'}
                      >
                        <StyledTableCell align="left">****${job.jobId.slice(-5)}</StyledTableCell>
                        <StyledTableCell align="left" className="bo-max-w-240">
                          <OverlayTrigger
                            trigger={
                              getTextWidthInOneLine(job.description) > 208 ? ['hover', 'focus'] : []
                            }
                            placement="top"
                            delay={{ show: 200 }}
                            overlay={
                              <Tooltip id={`job_desc_${job.jobId}`} className="tooltip-max-width">
                                {job.description}
                              </Tooltip>
                            }
                          >
                            <div className="text-truncate py-2">{job.description}</div>
                          </OverlayTrigger>
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Badge
                            className="bo-inline-badge"
                            bg={SYSTEM_JOB_STATUS_BADGE[job.status]?.color}
                          >
                            {SYSTEM_JOB_STATUS_BADGE[job.status]?.text}
                          </Badge>
                        </StyledTableCell>
                        <StyledTableCell align="left" className="text-nowrap">
                          {job.initTime
                            ? `${moment
                                .utc(job.initTime)
                                .local()
                                .format('MMM DD, YYYY HH:mm:ss (UTCZ)')}`
                            : '---'}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {getTimeDiff(job.startTime, job.queueTime)}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {getTimeDiff(job.endTime, job.startTime)}
                        </StyledTableCell>
                      </TableRow>
                      {selectedJobId === job.id && (
                        <TableRow hover={false} tabIndex={-1} className="bo-table-active-subrow">
                          <StyledTableCell align="left" className="w-auto" colSpan={6}>
                            {job.status === 'FAILED'
                              ? job.error
                              : renderResultsCell(job.type, job.results)}
                          </StyledTableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Spinner>
      </Card.Body>
    </Card>
  );
}

export default DashboardSystemJobs;
